import { useTranslation } from 'react-i18next';

import { useAdminListStore } from '../store/admin-list.store';
import type { Admin, AdminSortKey } from '../types/admin.types';
import { AdminListRow } from './AdminListRow';

interface AdminListTableProps {
  admins: readonly Admin[];
}

interface ColumnDef {
  key: 'account' | 'manage' | 'note' | AdminSortKey;
  i18nKey: string;
  width?: number;
  sortable: boolean;
}

// Column order + widths per PRD 15 §4.1 (Table column definition).
// 8 columns since 2026-05-26 (added `note` per prototype impact review).
// 'account', 'manage', and 'note' are not sortable.
const COLUMNS: readonly ColumnDef[] = [
  { key: 'id', i18nKey: 'id', width: 60, sortable: true },
  { key: 'account', i18nKey: 'account', width: 220, sortable: false },
  { key: 'name', i18nKey: 'name', width: 120, sortable: true },
  { key: 'role', i18nKey: 'role', sortable: true },
  { key: 'status', i18nKey: 'status', width: 100, sortable: true },
  { key: 'lastLoginAt', i18nKey: 'lastLogin', width: 160, sortable: true },
  { key: 'manage', i18nKey: 'manage', width: 80, sortable: false },
  { key: 'note', i18nKey: 'note', width: 140, sortable: false },
];

export function AdminListTable({ admins }: AdminListTableProps) {
  const { t } = useTranslation('admin');
  const sort = useAdminListStore((s) => s.sort);
  const toggleSort = useAdminListStore((s) => s.toggleSort);

  // Unicode sort indicator per decision 2 (Slice 8 may replace with lucide chevrons).
  const renderSortIcon = (col: ColumnDef): string => {
    if (!col.sortable) return '';
    if (sort.key !== col.key) return '↕';
    return sort.dir === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="card">
      <table className="table" style={{ margin: 0 }}>
        <thead>
          <tr>
            {COLUMNS.map((col) => (
              <th
                key={col.key}
                style={{
                  width: col.width,
                  cursor: col.sortable ? 'pointer' : undefined,
                }}
                onClick={
                  col.sortable
                    ? () => toggleSort(col.key as AdminSortKey)
                    : undefined
                }
              >
                {t(`adminManagement.list.columns.${col.i18nKey}`)}{' '}
                {col.sortable && <span className="sort-icon">{renderSortIcon(col)}</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {admins.map((admin) => (
            <AdminListRow key={admin.id} admin={admin} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// PRD §5: skeleton = 5 rows × 7 cols.
export function AdminListSkeleton() {
  const { t } = useTranslation('admin');
  return (
    <div className="card">
      <table className="table" style={{ margin: 0 }}>
        <thead>
          <tr>
            {COLUMNS.map((col) => (
              <th key={col.key} style={{ width: col.width }}>
                {t(`adminManagement.list.columns.${col.i18nKey}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 5 }).map((_, i) => (
            <tr key={i}>
              {COLUMNS.map((col) => (
                <td key={col.key}>
                  <div
                    className="animate-pulse"
                    style={{ height: 14, background: 'var(--muted)', borderRadius: 4 }}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AdminListEmpty() {
  const { t } = useTranslation('admin');
  return (
    <div className="card">
      <table className="table" style={{ margin: 0 }}>
        <thead>
          <tr>
            {COLUMNS.map((col) => (
              <th key={col.key} style={{ width: col.width }}>
                {t(`adminManagement.list.columns.${col.i18nKey}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td
              colSpan={COLUMNS.length}
              style={{
                textAlign: 'center',
                padding: '40px 0',
                color: 'var(--muted-foreground)',
              }}
            >
              {t('adminManagement.list.empty')}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
